import { Fragment, useEffect, useState } from "react";
import { ExternalLink, Settings2 } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { BoardField, FieldValue, FieldValues } from "@kandoo/shared";
import { api, type BoardDetail, type CardDetail } from "../api/client";
import { labelColor } from "../util/labelColors";

/** Set one card's field value, optimistically in both the card and board caches. */
export function useSetFieldValue(boardId: string) {
  const qc = useQueryClient();
  const apply = (cardId: string, fieldId: string, value: FieldValue | null) => {
    const next = (vals: FieldValues): FieldValues => {
      const out = { ...vals };
      if (value === null || value === "") delete out[fieldId];
      else out[fieldId] = value;
      return out;
    };
    qc.setQueryData<CardDetail>(["card", cardId], (c) =>
      c ? { ...c, fieldValues: next(c.fieldValues ?? {}) } : c,
    );
    qc.setQueryData<BoardDetail>(["board", boardId], (b) =>
      b
        ? {
            ...b,
            lists: b.lists.map((l) => ({
              ...l,
              cards: l.cards.map((c) =>
                c.id === cardId ? { ...c, fieldValues: next(c.fieldValues ?? {}) } : c,
              ),
            })),
          }
        : b,
    );
  };
  return useMutation({
    mutationFn: ({ cardId, fieldId, value }: { cardId: string; fieldId: string; value: FieldValue | null }) =>
      api.fields.setValue(cardId, fieldId, value),
    onMutate: async ({ cardId, fieldId, value }) => {
      await qc.cancelQueries({ queryKey: ["card", cardId] });
      await qc.cancelQueries({ queryKey: ["board", boardId] });
      apply(cardId, fieldId, value);
    },
    onSettled: (_r, _e, { cardId }) => {
      qc.invalidateQueries({ queryKey: ["card", cardId] });
      qc.invalidateQueries({ queryKey: ["board", boardId] });
    },
  });
}

/**
 * A text-ish input that keeps its own draft and commits on blur or Enter —
 * never per keystroke (a date input fires change on every digit of the year).
 * Follows the stored value while not focused, so a poll can't clobber typing.
 */
export function CommitInput({
  value,
  type,
  disabled,
  placeholder,
  ariaLabel,
  onCommit,
}: {
  value: string;
  type: "text" | "number" | "date" | "url";
  disabled: boolean;
  placeholder?: string;
  ariaLabel: string;
  onCommit: (v: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setDraft(value);
  }, [value, focused]);
  const commit = () => {
    if (draft.trim() !== value) onCommit(draft.trim());
  };
  return (
    <input
      type={type === "url" ? "url" : type}
      value={draft}
      disabled={disabled}
      placeholder={placeholder}
      aria-label={ariaLabel}
      onFocus={() => setFocused(true)}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        setFocused(false);
        commit();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          (e.target as HTMLInputElement).blur();
        } else if (e.key === "Escape") {
          // revert, and keep the card modal open
          e.stopPropagation();
          setDraft(value);
        }
      }}
      className="w-full min-w-0 rounded border border-slate-300 bg-[var(--surface)] px-2 py-1 text-sm disabled:opacity-60"
    />
  );
}

export function FieldEditor({
  field,
  value,
  canEdit,
  onChange,
}: {
  field: BoardField;
  value: FieldValue | undefined;
  canEdit: boolean;
  onChange: (v: FieldValue | null) => void;
}) {
  // Checkbox and dropdown are controlled inputs; the optimistic cache write
  // lands a tick later (after in-flight polls are cancelled), so mirror the
  // value locally or the control snaps back for a moment.
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  const change = (v: FieldValue | null) => {
    setLocal(v ?? undefined);
    onChange(v);
  };
  switch (field.type) {
    case "checkbox":
      return (
        <input
          type="checkbox"
          checked={local === true}
          disabled={!canEdit}
          aria-label={field.name}
          onChange={(e) => change(e.target.checked ? true : null)}
          className="h-4 w-4"
        />
      );
    case "select": {
      const opt = field.options.find((o) => o.id === local);
      return (
        <div className="flex items-center gap-2">
          <select
            value={opt?.id ?? ""}
            disabled={!canEdit}
            aria-label={field.name}
            onChange={(e) => change(e.target.value || null)}
            className="min-w-0 flex-1 rounded border border-slate-300 bg-[var(--surface)] px-2 py-1 text-sm disabled:opacity-60"
          >
            <option value="">—</option>
            {field.options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
          {opt && (
            <span
              aria-hidden
              className={`h-3 w-3 shrink-0 rounded-full ${labelColor(opt.color).swatch}`}
            />
          )}
        </div>
      );
    }
    case "url":
      return (
        <div className="flex items-center gap-1">
          <CommitInput
            type="url"
            value={typeof value === "string" ? value : ""}
            disabled={!canEdit}
            placeholder="https://…"
            ariaLabel={field.name}
            onCommit={(v) => onChange(v || null)}
          />
          {typeof value === "string" && value && (
            <a
              href={value}
              target="_blank"
              rel="noreferrer noopener"
              title="Open link"
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            >
              <ExternalLink size={14} />
            </a>
          )}
        </div>
      );
    case "number":
      return (
        <CommitInput
          type="number"
          value={value === undefined ? "" : String(value)}
          disabled={!canEdit}
          ariaLabel={field.name}
          onCommit={(v) => onChange(v === "" ? null : Number(v))}
        />
      );
    case "date":
      return (
        <CommitInput
          type="date"
          value={typeof value === "string" ? value : ""}
          disabled={!canEdit}
          ariaLabel={field.name}
          onCommit={(v) => onChange(v || null)}
        />
      );
    default:
      return (
        <CommitInput
          type="text"
          value={typeof value === "string" ? value : ""}
          disabled={!canEdit}
          ariaLabel={field.name}
          onCommit={(v) => onChange(v || null)}
        />
      );
  }
}

interface CardFieldsProps {
  boardId: string;
  cardId: string;
  fields: BoardField[];
  values: FieldValues;
  canEdit: boolean;
  /** Open the board's field manager. */
  onManage: () => void;
}

/** Card modal "Fields" section: one editor per board custom field. */
export function CardFields({ boardId, cardId, fields, values, canEdit, onManage }: CardFieldsProps) {
  const setValue = useSetFieldValue(boardId);
  if (fields.length === 0 && !canEdit) return null;
  return (
    <section className="mt-6">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="font-medium">Fields</h2>
        {canEdit && (
          <button
            type="button"
            onClick={onManage}
            title="Add or edit this board's fields"
            className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-1 text-sm text-slate-700 hover:bg-slate-200"
          >
            <Settings2 size={14} />
            {fields.length ? "Manage" : "Add fields"}
          </button>
        )}
      </div>
      {fields.length === 0 ? (
        <p className="text-sm text-slate-500">
          No custom fields yet. Add one to track things like priority, estimate or a link.
        </p>
      ) : (
        <div className="grid grid-cols-[minmax(6rem,auto)_1fr] items-center gap-x-3 gap-y-2">
          {fields.map((f) => (
            <Fragment key={f.id}>
              <span className="truncate text-sm text-slate-600" title={f.name}>
                {f.name}
              </span>
              <FieldEditor
                field={f}
                value={values[f.id]}
                canEdit={canEdit}
                onChange={(value) => setValue.mutate({ cardId, fieldId: f.id, value })}
              />
            </Fragment>
          ))}
        </div>
      )}
    </section>
  );
}
